import { useEffect, useState } from "react";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Loading from "@components/loading/loading";

import { AutosaveStatus as Status } from "./use-autosave";

// How long a save has to run before it's worth saying so. Most finish well
// inside this, and swapping "Saved" for a spinner and back on every pause in
// typing reads as the page flickering.
const slowSaveDelayMs = 600;

// Replaces the Save button on autosaving pages: says what just happened, and
// offers a retry when a save failed.
const AutosaveStatus = ({
  status,
  onRetry,
  onReload,
}: {
  status: Status;
  onRetry: () => void;
  // Given once what the page shows was changed elsewhere while it had unsaved
  // edits: those can't be saved over the change, so it offers a reload.
  onReload?: () => void;
}) => {
  const isSaving = status === "saving";
  const [isSlowSave, setIsSlowSave] = useState(false);

  useEffect(() => {
    if (!isSaving) {
      setIsSlowSave(false);
      return;
    }
    const timeout = setTimeout(() => setIsSlowSave(true), slowSaveDelayMs);
    return () => clearTimeout(timeout);
  }, [isSaving]);

  return (
    // A fixed slot, so what's beside it never moves as the label changes.
    <div className="flex justify-end items-center shrink-0 min-w-20">
      {onReload ? (
        <div
          className="flex items-center gap-2 text-sm text-amber-700"
          aria-label="Changed elsewhere"
        >
          Changed elsewhere
          <Button
            size={ButtonSize.medium}
            theme={buttonTheme.text}
            text="Reload"
            tooltip="Topicflow AI or someone else changed this page. Reload to see it. Edits made here since then can't be saved over it."
            onClick={onReload}
          />
        </div>
      ) : status === "error" ? (
        <div className="flex items-center gap-2 text-sm text-rose-600">
          Couldn&apos;t save changes
          <Button
            size={ButtonSize.medium}
            theme={buttonTheme.text}
            text="Retry"
            onClick={onRetry}
          />
        </div>
      ) : isSaving && isSlowSave ? (
        <div
          className="flex items-center gap-2 text-sm text-gray-500"
          aria-label="Saving changes"
        >
          <Loading mini size={4} />
          Saving…
        </div>
      ) : status === "saved" ? (
        // Only once everything is saved: edits waiting for their save, or in
        // a quick one, aren't yet. Those show nothing rather than blink
        // through a spinner.
        <div className="text-sm text-gray-500" aria-label="Changes saved">
          Saved
        </div>
      ) : null}
    </div>
  );
};

export default AutosaveStatus;
